<template>
  <div class="address-details">
    <div class="search_wrapper">
      <div class="search">
        <SearchInput
          v-model="searchValue"
          @search="onSearch(searchValue)"
          :placeholder="$t('SearchPlaceholder')"
        />
      </div>
      <div class="info">
        <div class="info_left">
          <Text pattern="h4" :weight="600">
            {{ $t("AddressInfo") }}
          </Text>
          <Text pattern="body3" :weight="500" color="#787878">
            <span class="bold">{{ $t("Address") }}:</span>
            {{ address }}
          </Text>
        </div>
        <div class="info_right">
          <BaseButton type="monochrome" class="button" @click="copyAddress">
            <BaseSvgIcon name="copy" class="icon" />
            {{ $t("CopyLink") }}
          </BaseButton>
          <BaseButton type="monochrome" class="button" @click="shareAddress">
            <BaseSvgIcon name="share" class="icon" />
            {{ $t("ShareIt") }}
          </BaseButton>
          <BaseButton
            type="monochrome"
            class="button"
            @click="goToWallet(event, $i18n.locale)"
          >
            {{ $t("GoToWallet") }}
            <BaseSvgIcon name="emcd" class="icon" />
          </BaseButton>
        </div>
      </div>
      <div class="section">
        <template v-if="isLoading">
          <BaseLoading class="loading" />
        </template>
        <template v-else-if="error">
          <StatusFail :text="error" />
        </template>
        <template v-else>
          <div class="section">
            <div>
              <div class="address-details_content">
                <TxDetails
                  :name="details?.network"
                  :img="coinLogo"
                  :address="address"
                />
                <accordion-details
                  :details="blockDetails"
                  :item="details"
                  is-third
                />
              </div>
              <transactions-table
                :items="details?.transactions"
                :decimals="decimals"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { BaseLoading, Text, SearchInput } from "@/shared/ui";
import BaseButton from "@emcd-shared/base-button/BaseButton.vue";
import BaseSvgIcon from "@/shared/ui/BaseSvgIcon/BaseSvgIcon.vue";
import { AccordionDetails } from "@/widgets/base";
import TransactionsTable from "@/widgets/AddressDetails/ui/TransactionsTable.vue";
import { amount as formatAmount } from "@/shared/utils/format/amount";
import { CoinShortcuts } from "@/widgets/base/data/enums";
import StatusFail from "@/shared/ui/StatusFail/ui/StatusFail.vue";
import TxDetails from "@/widgets/AddressDetails/ui/TxDetails.vue";

import { goToWallet } from "@/shared/helpers";

export default {
  name: "AddressDetails",
  components: {
    TxDetails,
    StatusFail,
    TransactionsTable,
    AccordionDetails,
    BaseLoading,
    Text,
    SearchInput,
    BaseButton,
    BaseSvgIcon,
  },
  data() {
    return {
      formatAmount,
      isLoading: false,
      isError: false,
      searchValue: "",
      error: null,
      details: null,
      address: this.$route.params.id,
      blockDetails: [
        {
          label: this.$t("AddressDetails | Balances"),
          field: "balances.amount",
          calculate: (item) => {
            if (!item?.balances[CoinShortcuts[item.network]]) {
              return "";
            }

            const sum = item.balances[CoinShortcuts[item.network]];

            return `${this.formatAmount(sum.amount || 0, sum.decimals || 1)} ${sum.symbol}`;
          },
        },
        {
          label: this.$t("AddressDetails | TotalReceived"),
          field: "total_received",
          calculate: (item) => {
            if (!item?.stats?.total_received) {
              return "";
            }
            return `${formatAmount(item.stats.total_received, this.decimals)} ${this.coinSymbol(item)}`;
          },
        },
        {
          label: this.$t("AddressDetails | TotalSent"),
          field: "stats.total_sent",
          calculate: (item) => {
            if (!item?.stats?.total_sent) {
              return "";
            }

            return `${formatAmount(item.stats.total_sent, this.decimals)} ${this.coinSymbol(item)}`;
          },
        },
        {
          label: this.$t("AddressDetails | TransactionCount"),
          field: "stats.transaction_count",
        },
      ],
    };
  },
  computed: {
    coinLogo() {
      if (!this.details?.network) {
        return null;
      }

      return "";
    },

    decimals() {
      if (
        !this.details?.balances ||
        !this.details?.balances[CoinShortcuts[this.details?.network]]
      ) {
        return 1;
      }
      return this.details?.balances[CoinShortcuts[this.details?.network]]
        .decimals;
    },
  },
  methods: {
    ...mapActions("explorer", ["GET_ADDRESS", "SEARCH"]),
    ...mapActions("alerts", ["ADD_ALERT"]),
    onSearch(search) {
      console.log(search, "search");
      if (search.trim().length) {
        this.$router.push({
          path: `/`,
          query: { q: search.trim() },
        });
      }
    },
    copyAddress() {
      navigator.clipboard.writeText(this.address);
      this.ADD_ALERT(this.$t("AddressCopied"));
    },
    async shareAddress() {
      try {
        await navigator.share({
          title: "Address",
          text: this.address,
        });
      } catch (err) {
        /* empty */
      }
    },
    goToWallet,
    formatDate(dateStr) {
      // Используем moment.js с учетом текущей локали
      const moment = require("moment");
      moment.locale(this.$i18n.locale);
      return moment(dateStr).format("DD MMM YYYY HH:mm:ss");
    },
    coinSymbol(item) {
      if (!item?.balances[CoinShortcuts[item.network]]) {
        return "";
      }

      const sum = item.balances[CoinShortcuts[item.network]];

      return sum.symbol || "";
    },
  },
  async mounted() {
    this.isLoading = true;
    const { params } = this.$route;
    const { id: address, network } = params;
    this.address = address;
    this.searchValue = address;

    if (!address || !network) {
      this.error = "AddressDetails | InvalidAddress";
      this.isLoading = false;
      return;
    }

    try {
      const response = await this.GET_ADDRESS({
        network,
        address,
      });
      console.log("response", response);
    } catch (error) {
      this.error =
        error?.response?.data?.error || error?.message || "An error occurred";
    } finally {
      this.isLoading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.address-details {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 16px;

  .loading {
    align-self: center;
    margin-top: 50px;
  }

  &_content {
    display: flex;
    flex-direction: row;
    gap: 24px;
    margin-bottom: 24px;
  }
}

.search_wrapper {
  display: flex;
  flex-direction: column;
}

.search {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 32px 0;
}

.info {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 28px 0 24px;

  &_left {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }

  &_right {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
  }
}

.bold {
  font-weight: 600;
}

.button {
  display: flex;
  align-items: center;
  gap: 10px;
}

.icon {
  width: 16px;
  height: 16px;
}

.section {
  .section-title {
    margin-bottom: 12px;
  }
}

.balances {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  .balance-item {
    padding: 12px 16px;
    background: var(--Dark-900, #111);
    border: 1px solid var(--Dark-850, #1a1a1a);
    border-radius: 8px;
  }
}

.stats {
  .stats-items {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    .stat-item {
      padding: 12px 16px;
      background: var(--Dark-900, #111);
      border: 1px solid var(--Dark-850, #1a1a1a);
      border-radius: 8px;
      min-width: 100px;
    }
  }
}

.transactions {
  .transactions-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .transaction-item {
    padding: 12px 16px;
    background: var(--Dark-900, #111);
    border: 1px solid var(--Dark-850, #1a1a1a);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .tx-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .tx-hash {
    word-break: break-all;
  }
  .tx-status {
    text-transform: capitalize;
  }
}

@media (max-width: 768px) {
  .address-details {
    &_content {
      flex-direction: column;
    }

    .tx {
      max-width: 100%;
    }
  }

  .info {
    &_right {
      display: none;
    }
  }
}
</style>
